import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { allowedCategories } from '@/config/categories';
import { safeLog } from '@/lib/safeLog';
import { RISK_CATEGORIES, RiskCategoryKey } from '@/lib/recallUtils';
import { getRetailerKeywords } from '@/config/retailerKeywords';
import { getRegionKeywords } from '@/config/regionKeywords';

const PAGE_SIZE = 50;

export type PeriodFilter = '7d' | '30d' | '90d';

export interface PaginatedRecallFilters {
  category: 'all' | 'baby-food';
  categories?: string[]; // Multi-select product categories (category_id)
  bacteria: string[];
  riskType: 'all' | 'mycotoxine_aflatoxine' | 'mycotoxine_ochratoxine' | 'pesticide_eto' | 'chimique_moah_pah' | 'conditionnement';
  riskCategory?: 'all' | RiskCategoryKey;
  severity: 'all' | 'high' | 'medium' | 'low';
  pregnancyOnly: boolean;
  retailers: string[]; // Multi-select retailers
  regions: string[]; // Multi-select regions
  agents?: string[];
  period: PeriodFilter;
  search?: string; // Server-side text search on product name / brand
}

export interface Recall {
  id: string;
  product_name: string;
  brand: string;
  risk_type: string;
  severity_score: number;
  pregnancy_flag: boolean;
  batch_lot: string | null;
  dlc_ddm: string | null;
  distribution_area: string[];
  retailer: string[];
  action_required: string | null;
  published_at: string;
  category_id: string;
  images: string[];
  gtin: string | null;
  official_url: string | null;
  source_url: string | null;
  applicability?: {
    pregnancy: boolean;
    baby: boolean;
    mother: boolean;
    extendedRisk?: boolean;
  };
}

type LooseRecallQuery = {
  in: (column: string, values: readonly (string | number)[]) => LooseRecallQuery;
  eq: (column: string, value: string | number | boolean) => LooseRecallQuery;
  or: (filters: string) => LooseRecallQuery;
  gte: (column: string, value: string) => LooseRecallQuery;
  order: (column: string, options: { ascending: boolean }) => LooseRecallQuery;
  range: (from: number, to: number) => PromiseLike<{
    data: Recall[] | null;
    error: { message: string } | null;
    count: number | null;
  }>;
};

// extend_risk_mode is read once per user and reused for 10 minutes instead of
// on every page of results.
const EXTENDED_MODE_TTL_MS = 10 * 60 * 1000;
let extendedModeCache: { userId: string; value: boolean; at: number } | null = null;

async function getExtendedMode(userId: string): Promise<boolean> {
  if (extendedModeCache && extendedModeCache.userId === userId && Date.now() - extendedModeCache.at < EXTENDED_MODE_TTL_MS) {
    return extendedModeCache.value;
  }
  const { data: profile } = await supabase
    .from('profiles')
    .select('extend_risk_mode')
    .eq('id', userId)
    .maybeSingle();
  const value = profile?.extend_risk_mode || false;
  extendedModeCache = { userId, value, at: Date.now() };
  return value;
}

const fetchRecallsPage = async (
  filters: PaginatedRecallFilters,
  pageParam: number
): Promise<{ recalls: Recall[]; nextCursor: number | null; totalCount: number }> => {
  // Get user's extend_risk_mode from profiles
  let extendedMode = false;
  let isAuthenticated = false;
  try {
    // getSession() reads the local session (no auth round-trip per page).
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (user) {
      isAuthenticated = true;
      extendedMode = await getExtendedMode(user.id);
    }
  } catch (error) {
    safeLog.error('[useRecallsPaginated] Error fetching extend_risk_mode', error);
  }

  // Calculate cutoff date based on period filter
  const periodDays = { '7d': 7, '30d': 30, '90d': 90 };
  const daysAgo = periodDays[filters.period];
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - daysAgo);

  // Étape 1: Récupérer les IDs filtrés par région et enseigne via RPC
  let regionFilteredIds: string[] | null = null;
  let retailerFilteredIds: string[] | null = null;

  // Filtrage par région via RPC (recherche textuelle)
  if (filters.regions && filters.regions.length > 0) {
    safeLog.debug('[useRecallsPaginated] Filtering regions via RPC:', filters.regions);
    const regionKeywords = getRegionKeywords(filters.regions);
    
    const { data: regionIds, error: regionError } = await supabase
      .rpc('search_by_region_text', { keywords: regionKeywords });
    
    if (regionError) {
      safeLog.error('[useRecallsPaginated] Region RPC error', regionError);
    } else {
      regionFilteredIds = regionIds || [];
    }
  }

  // Filtrage par enseigne via RPC (recherche textuelle)
  if (filters.retailers && filters.retailers.length > 0) {
    safeLog.debug('[useRecallsPaginated] Filtering retailers via RPC:', filters.retailers);
    const retailerKeywords = getRetailerKeywords(filters.retailers);
    
    const { data: retailerIds, error: retailerError } = await supabase
      .rpc('search_by_retailer_text', { keywords: retailerKeywords });
    
    if (retailerError) {
      safeLog.error('[useRecallsPaginated] Retailer RPC error', retailerError);
    } else {
      retailerFilteredIds = retailerIds || [];
    }
  }

  // Calculer l'intersection des IDs si les deux filtres sont actifs
  let combinedFilteredIds: string[] | null = null;
  if (regionFilteredIds !== null && retailerFilteredIds !== null) {
    const regionSet = new Set(regionFilteredIds);
    combinedFilteredIds = retailerFilteredIds.filter(id => regionSet.has(id));
  } else if (regionFilteredIds !== null) {
    combinedFilteredIds = regionFilteredIds;
  } else if (retailerFilteredIds !== null) {
    combinedFilteredIds = retailerFilteredIds;
  }

  // Si le filtre retourne un tableau vide, retourner directement
  if (combinedFilteredIds !== null && combinedFilteredIds.length === 0) {
    return { recalls: [], nextCursor: null, totalCount: 0 };
  }

  // Étape 2: Construire la requête principale
  // L'embed recall_user_state n'est accessible qu'aux utilisateurs connectés
  const selectClause = isAuthenticated
    ? `*,
      recall_user_state!left (
        user_id,
        recall_id,
        state,
        updated_at
      )`
    : '*';

  // The select clause is dynamic (auth-dependent embed), so PostgREST's generic
  // inference can't resolve it: use a loose builder contract instead.
  let query = supabase
    .from('recalls')
    .select(selectClause, { count: 'exact' }) as unknown as LooseRecallQuery;

  // Appliquer le filtre d'IDs si disponible
  if (combinedFilteredIds !== null) {
    query = query.in('id', combinedFilteredIds);
  }

  // Apply period filter
  query = query.gte('published_at', cutoffDate.toISOString());

  // Text search (server-side, product name or brand)
  const term = (filters.search || '').trim().replace(/[,()%*\\]/g, ' ').trim();
  if (term.length >= 2) {
    query = query.or(`product_name.ilike.*${term}*,brand.ilike.*${term}*`);
  }

  // Determine allowed categories based on extended mode
  const cats = allowedCategories({ extended: extendedMode });

  // Apply category filter
  if (filters.categories && filters.categories.length > 0) {
    // Advanced category filter takes precedence
    query = query.in('category_id', filters.categories);
  } else if (filters.category === 'baby-food') {
    query = query.eq('category_id', '25');
  } else {
    const pathogenicPilotCategories = ['18', '19', '20', '21'];
    const allCategories = [...new Set([...cats, ...pathogenicPilotCategories])];
    query = query.in('category_id', allCategories);
  }

  if (filters.bacteria && filters.bacteria.length > 0) {
    query = query.in('risk_type', filters.bacteria);
  }
  
  if (filters.agents && filters.agents.length > 0) {
    query = query.in('risk_type', filters.agents);
  }

  if (filters.riskType !== 'all') {
    query = query.eq('risk_type', filters.riskType);
  }

  if (filters.riskCategory && filters.riskCategory !== 'all') {
    const categoryTypes = RISK_CATEGORIES[filters.riskCategory as RiskCategoryKey].types;
    query = query.in('risk_type', categoryTypes);
  }

  if (filters.pregnancyOnly) {
    query = query.eq('pregnancy_applicable', true);
  }

  if (filters.severity !== 'all') {
    const severityMap = { high: [4, 5], medium: [3], low: [1, 2] };
    const scores = severityMap[filters.severity];
    query = query.in('severity_score', scores);
  }

  const { data, error, count } = await query
    .order('published_at', { ascending: false })
    .order('severity_score', { ascending: false })
    .range(pageParam, pageParam + PAGE_SIZE - 1);

  if (error) {
    safeLog.error('[useRecallsPaginated] Supabase error', error);
    throw error;
  }

  const recalls = data?.map((item) => ({
    ...item,
    applicability: item.applicability,
  })) || [];

  const totalCount = count || 0;
  const hasMore = pageParam + PAGE_SIZE < totalCount;

  return {
    recalls,
    nextCursor: hasMore ? pageParam + PAGE_SIZE : null,
    totalCount
  };
};

export const useRecallsPaginated = (filters: PaginatedRecallFilters) => {
  return useInfiniteQuery({
    queryKey: ['recalls-paginated', filters],
    queryFn: ({ pageParam }) => fetchRecallsPage(filters, pageParam),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 0,
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnWindowFocus: true,
  });
};

export { PAGE_SIZE };
